import { Device } from '@twilio/voice-sdk';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { attachRemoteAudio, unlockTwilioAudio } from '../../twilio_agent/twilio_audio';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }

  return get_api_url(endpoint);
};

const getToken = async () => {
  const url = getUrl('/v2/twilio_call/token');
  const resp = await get_api(url, true);
  return resp.data?.data?.token;
};

export const resolveContact = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/resolve_contact')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data;
};

export const getOutgoingCallerId = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/outgoing_caller_id')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.from_number;
};

// prepareOutbound resolves a typed phone number for the dialer-pad flow.
// Returns { student_uuid, name, from_number, to_number }; student_uuid/name
// are empty strings when no student matches phone_personal.
export const prepareOutbound = async (toNumber) => {
  const url = `${getUrl('/v2/twilio_call/prepare_outbound')}?to=${encodeURIComponent(toNumber)}`;
  const resp = await get_api(url, true);
  return resp.data?.data;
};

export const listCalls = async (studentUuid) => {
  const url = `${getUrl('/v2/twilio_call/calls')}?student_uuid=${encodeURIComponent(studentUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.calls || [];
};

export const getRecordingURL = async (callUuid) => {
  const url = `${getUrl('/v2/twilio_call/recording_url')}?call_uuid=${encodeURIComponent(callUuid)}`;
  const resp = await get_api(url, true);
  return resp.data?.data?.url;
};

/**
 * Start an outbound call via Twilio Voice SDK.
 * onStatus is invoked with one of: connecting | ringing | connected | failed | ended.
 * Returns an object with { hangup, getStatus, teardown, toggleMute } the
 * caller uses to drive and reconcile the call.
 */
export const startCall = async ({ toNumber, fromNumber, studentUuid, onStatus, onMuteChange }) => {
  onStatus('connecting');

  const token = await getToken();
  if (!token) throw new Error('Failed to fetch Twilio token');

  const device = new Device(token);
  // The dial click's activation window is already gone by here (token +
  // contact lookups awaited above), so this only succeeds on a context some
  // gesture has unlocked — GlobalOutboundCallManager does that on the click.
  unlockTwilioAudio();

  const params = { To: toNumber };
  if (fromNumber) params.CallerId = fromNumber;
  if (studentUuid) params.StudentUUID = studentUuid;
  const call = await device.connect({ params });

  const detachRemoteAudio = attachRemoteAudio(call);

  call.on('ringing', () => onStatus('ringing'));
  call.on('accept', () => onStatus('connected'));
  call.on('disconnect', () => {
    onStatus('ended');
    detachRemoteAudio();
    device.destroy();
  });
  call.on('cancel', () => {
    onStatus('ended');
    detachRemoteAudio();
    device.destroy();
  });
  call.on('error', (err) => {
    console.error('Twilio call error', err);
    onStatus('failed');
    detachRemoteAudio();
    device.destroy();
  });
  call.on('mute', (isMuted) => {
    if (onMuteChange) onMuteChange(!!isMuted);
  });

  return {
    hangup: () => {
      try {
        call.disconnect();
      } catch (e) {
        console.error('hangup error', e);
      }
    },
    // The SDK's own view of the call. Surfaced so callers can reconcile their
    // state against it: the terminal handlers above are not guaranteed to
    // fire (a dead socket, a slept machine, or a discarded tab can swallow
    // 'disconnect'/'cancel'/'error' entirely). Returns a Call.State string —
    // 'closed' means the call is over — or null if the SDK can't tell us.
    getStatus: () => {
      try {
        return call.status();
      } catch (e) {
        return null;
      }
    },
    // Force-release the call and its Device. The terminal handlers above
    // normally destroy the Device; callers need this for the path where they
    // never fired, otherwise the Device stays registered and keeps receiving
    // invites for a call the UI has already given up on.
    teardown: () => {
      try {
        call.disconnect();
      } catch (e) {
        /* noop — caller already logged why it's forcing teardown */
      }
      detachRemoteAudio();
      try {
        device.destroy();
      } catch (e) {
        /* noop */
      }
    },
    toggleMute: () => {
      try {
        const next = !call.isMuted();
        call.mute(next);
        return next;
      } catch (e) {
        console.error('mute error', e);
        return false;
      }
    }
  };
};
